import { HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";

import { Product } from "@hermes/api-model-product";
import { Context, INFRA_SETTINGS, WINDOW } from "@hermes/app-core";
import { InfraSettings } from "@hermes/env-infra";
import { AlternateService } from "@hermes/fragments/alternates";
import {
    PRODUCT_IN_GRID_DESKTOP,
    PRODUCT_IN_GRID_MOBILE,
} from "@hermes/fragments/product-utils";
import { Logger } from "@hermes/logger";
import { formatAssetUrl } from "@hermes/utils/helpers";
import { SCENE7_IMAGE_BACKGROUND_COLOR } from "@hermes/utils-generic/constants";
import { HeadService } from "@hermes/utils-generic/services/head";
import { LOGGER } from "@hermes/web-logger";

import { Facet, GridCategory, GridCategoryMetas } from "../../model/grid.model";

import {
    getAlternatePathsForCategory,
    getCanonicalUrlForCategory,
    getDescriptionForCategory,
} from "./grid-page-head.helper";

const SEARCH_TITLE = $localize`:@@hermes-search-result.search-results:Search results for`;

@Injectable()
export class GridPageHeadService {
    constructor(
        @Inject(LOGGER) private logger: Logger,
        @Inject(INFRA_SETTINGS) private settings: InfraSettings,
        private headService: HeadService,
        private alternateService: AlternateService,
        private context: Context,
        @Inject(WINDOW) private window: Window,
    ) {}

    /**
     * Update head tag for grid page in search mode
     */
    public updateHeadTagSearchPage(searchTerm?: string): void {
        if (searchTerm) {
            this.headService.setTitle(`${SEARCH_TITLE} "${searchTerm}"`);
        }
        this.alternateService.addGenericAlternatesUrls();
        this.headService.addNoIndexMetaInformation();
    }

    /**
     * Add SEO pagination head links for grid page
     */
    public addSeoPaginationHeadLinks(
        previousPageNumber?: number,
        nextPageNumber?: number,
    ): void {
        if (previousPageNumber) {
            this.headService.addSeoPreviousPageUrl(previousPageNumber);
        }
        if (nextPageNumber) {
            this.headService.addSeoNextPageUrl(nextPageNumber);
        }
    }

    /**
     * Remove SEO pagination head links for grid page
     */
    public removeSeoPaginationHeadLinks(): void {
        this.headService.cleanSeoPaginationUrls();
    }

    /**
     * Update head tag for grid page in category mode
     */
    public updateHeadTagCategoryPage(params: {
        category: GridCategory;
        categoryMetas: GridCategoryMetas;
        combinedFacetActivated: boolean;
        facet: Facet;
        seoPageParam: number;
    }): void {
        this.headService.cleanExistingMetas();
        if (params.categoryMetas || params.facet) {
            this.addMetaTagsCategory(params);
        } else {
            this.logger.warn(
                `SEO - no meta tags for category : ${params.category.pimCode}`,
            );
        }
    }

    /**
     * Add preload links for the first product at grid image size.
     * url in link constructed as in constructProductGridImageUrl() method of image-resizer.
     */
    public addPreloadImageLink(product: Product, isMobile: boolean): void {
        if (!product || !product.assets || product.assets.length === 0) {
            return;
        }
        const imageSize = isMobile
            ? String(PRODUCT_IN_GRID_MOBILE)
            : String(PRODUCT_IN_GRID_DESKTOP);

        const cleanedAssetUrl = formatAssetUrl(
            product.assets[0].url,
            this.context.isInBrowserMode()
                ? this.window.navigator.userAgent
                : undefined,
        );
        const imageUrl = `${cleanedAssetUrl}&${SCENE7_IMAGE_BACKGROUND_COLOR}&${new HttpParams()
            .set("wid", imageSize)
            .set("hei", imageSize)
            .toString()}`;

        this.headService.cleanImagePreloadUrls();
        this.headService.addPreloadImageLink({ href: imageUrl });
    }

    /**
     * Add metatags for category
     */
    private addMetaTagsCategory(params: {
        category: GridCategory;
        categoryMetas: GridCategoryMetas;
        combinedFacetActivated: boolean;
        facet: Facet;
        seoPageParam: number;
    }): void {
        const facet = params.facet;
        const metaTitle = facet?.facetTitle || params.categoryMetas.title;
        const frontendUrl = this.settings.frontend;
        const canonicalUrl = getCanonicalUrlForCategory(frontendUrl, params);
        const description = getDescriptionForCategory(params);
        const altPaths = getAlternatePathsForCategory(params);

        this.headService.setTitle(metaTitle);
        this.headService.addCanonicalUrl(canonicalUrl);
        this.alternateService.addAlternatesPaths(altPaths);
        this.headService.addDescriptionMetaInformations(description);
        this.headService.addItemPropMetaInformations(metaTitle, description);
        this.headService.addOgMetaInformations(
            "website",
            metaTitle,
            description,
            canonicalUrl,
        );
        this.headService.addTwitterMetaInformations(
            "summary",
            metaTitle,
            description,
            canonicalUrl,
        );
    }
}
